import 'bootstrap/dist/css/bootstrap.min.css';
import './assets/css/components/About.css';
import './assets/css/dashboards/News.css';
import './assets/css/dashboards/Pricing.css';
import './assets/css/dashboards/Settings.css';
import './assets/css/dashboards/UserDashboard.css';
import './assets/css/general/App.css';
import './assets/css/general/Buttons.css';
import './assets/css/general/Content.css';
import './assets/css/general/Extra.css';
import './assets/css/general/Forms.css';
import './assets/css/general/Menu.css';
import './assets/css/general/Modal.css';
import './assets/css/general/Utilities.css';
import './assets/css/Inputs.css';
import './assets/css/Leaflet.css';

// core styles are required for all packages
import '@mantine/core/styles.css';
// other css files are required only if
// you are using components from the corresponding package
// import '@mantine/dates/styles.css';
// import '@mantine/dropzone/styles.css';
// import '@mantine/code-highlight/styles.css';

import { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { Link, Route, Routes, useLocation } from 'react-router-dom';
import { ReturnPropsAPIGetUserLoginResponse } from './api/APIUsers';
import { APIGetUserSubscriptionPlanResponse, ReturnPropsAPIGetUserSubscriptionPlanResponse } from './api/Common';
import { useAppDispatch, useAppSelector } from './AppHooks';
import useScript from './assets/components/Common';
import { SubscriptionPlan } from './assets/components/data/SubscriptionData';
import { Button } from './assets/components/elements/Button';
import Header from './assets/components/elements/Header';
import Loading from './assets/components/elements/Loading';
import ContactForm from './assets/components/forms/ContactForm';
import LoginForm from './assets/components/forms/LoginForm';
import LoginVerifyOTPForm from './assets/components/forms/LoginVerifyOTPForm';
import PrivateRoute from './assets/components/privateRoute';
import { setSubscriptionStatus } from './slices/UserSlice';
import { IUserProps } from './views/Dashboard';
import RouteNotFound from './views/RouteNotFound';
import RegisterForm from './assets/components/forms/RegisterForm';

const Home = lazy(() => import('./views/Home'));
const About = lazy(() => import('./views/About'));
const Services = lazy(() => import('./views/Services'));
const Dashboard = lazy(() => import('./views/Dashboard'));
const GeneralDashboard = lazy(() => import('./views/GeneralDashboard'));
const GenericOkayForm = lazy(() => import('./assets/components/forms/GenericOkayForm'));
const Pricing = lazy(() => import('./views/Pricing'));
const Weather = lazy(() => import('./views/Weather'));
const Settings = lazy(() => import('./views/Settings'));
const SettingsAccountInformation = lazy(() => import('./assets/components/elements/dashboardElements/SettingsAccountInformation'));
const SettingsPreferences = lazy(() => import('./assets/components/elements/dashboardElements/SettingsPreferences'));
const SettingsNotifications = lazy(() => import('./assets/components/elements/dashboardElements/SettingsNotifications'));
const SettingsSecurity = lazy(() => import('./assets/components/elements/dashboardElements/SettingsSecurity'));
const SettingsPrivacyAndData = lazy(() => import('./assets/components/elements/dashboardElements/SettingsPrivacyAndData'));
const SettingsHelp = lazy(() => import('./assets/components/elements/dashboardElements/SettingsHelp'));
const SettingsFeedback = lazy(() => import('./assets/components/elements/dashboardElements/SettingsFeedback'));
const SettingsAbout = lazy(() => import('./assets/components/elements/dashboardElements/SettingsAbout'));
const PrivacyPolicy = lazy(() => import('./views/PrivacyPolicy'))
const PasswordPreResetForm = lazy(() => import('./assets/components/forms/PasswordPreResetForm'));
const PasswordResetOTPForm = lazy(() => import('./assets/components/forms/PasswordResetOTPForm'));
const PasswordResetForm = lazy(() => import('./assets/components/forms/PasswordResetForm'));
const Modal = lazy(() => import('./assets/components/elements/Modal'));
const FeedbackForm = lazy(() => import('./assets/components/forms/FeedbackForm'));
const ActionPanel = lazy(() => import('./assets/components/utilities/ActionPanel'));

// https://react-icons.github.io/react-icons

function App() {
	const dispatch = useAppDispatch();
	const userPlan = useAppSelector(state => state.users.subscriptionPlan);
	const [isContactFormOpen, setContactFormOpen] = useState(false);
	const [isContactSubmitFormOpen, setContactSubmitFormOpen] = useState(false);
	const [isFeedbackFormOpen, setFeedbackFormOpen] = useState(false);
	const [isFeedbackSubmitFormOpen, setFeedbackSubmitFormOpen] = useState(false);
	const [isFooterShowing, setFooterShowing] = useState(false);
	const [apiLoginData, setAPILoginData] = useState<ReturnPropsAPIGetUserLoginResponse>(null);
	const isLoggedIn = useAppSelector(state => state.users.isLoggedIn);
	const appContentRef = useRef<HTMLDivElement>(null);
	const footerRef = useRef<HTMLDivElement>(null);
	const location = useLocation();

	useEffect(() => {		
		// Reset footer variables and its showing state when location changes
		if (footerRef.current && footerRef.current.style) {
			// console.log("Footer hidden on location change");
			
			footerRef.current.style.transition = 'bottom .5s';
			footerRef.current.style.bottom = '-40%';
		}

		// Reset scroll position to top of page
		if (appContentRef.current) {
			// console.log("Scrolling to top of page");
			
			appContentRef.current.scrollTo(0, 0);
		}

		setFooterShowing(false);
	}, [location]);

	const handleContainerScroll = (event: React.UIEvent<HTMLDivElement> | Event) => {
		try {		
			// Log event details to see if we can find non-user fired events
			// console.log("Event: ", event);
			const container = appContentRef.current;
			// console.log("Footer bottom transition: ", footerRef.current.style.transition);
			
			if (container) {
				const { scrollTop, scrollHeight, clientHeight } = container;

				if (scrollTop + clientHeight >= scrollHeight) {
					// Reached bottom
					// console.log("Footer shown");
					setFooterShowing(true);
					// Show that the footer exists by setting it to -35% from the bottom
					// NOTE: If footerRef.current.style.bottom is not set to -40% in the footer, then it was 
					// NOTE: already showing and we can skip the transition and bottom setting.
					footerRef.current.style.transition = 'bottom .5s';
					if (footerRef.current.style.bottom !== '-40%') {
						// console.log("Footer bottom: ", footerRef.current.style.bottom);
					} else {
						// console.log("Footer shown and is out of screen: ", footerRef.current.style.bottom);
						footerRef.current.style.bottom = '-35%';
					}
				}
			}
		} catch (error) {
			console.log("Error in handle container scroll: ", error);
		}
	};

	const handleGeneralScroll = (event: React.WheelEvent<HTMLDivElement> | WheelEvent) => {
		try {
			if (isFooterShowing) {
				// Scrolling
				// Bottom is naturally set to -40% for the footer. Add 1% every scroll to make it appear as the user scrolls down.
				let currentBottom = footerRef.current.style.bottom
				// console.log("Current bottom: ", parseInt(currentBottom));
				
				if (parseInt(currentBottom) < 0 && event.deltaY > 0) {
					// console.log("Current bottom and event deltaY (down): ", currentBottom, event.deltaY);
					footerRef.current.style.bottom = (parseInt(currentBottom) + 5) + '%';
				} else if (parseInt(currentBottom) > -40 && event.deltaY < 0) {
					// console.log("Current bottom and event deltaY (up): ", currentBottom, event.deltaY);
					
					footerRef.current.style.transition = 'bottom .5s';
					footerRef.current.style.bottom = '-40%';

					// On transition complete, set footer to not showing
					setTimeout(() => {
						if (footerRef.current && footerRef.current.style && footerRef.current.style.bottom) {
							if (parseInt(footerRef.current.style.bottom) <= -40) {
								// console.log("Footer hidden");
								// console.log("Footer bottom at timeout: ", footerRef.current.style.bottom);
								// console.log("Footer bottom at timeout int: ", parseInt(footerRef.current.style.bottom));
								setFooterShowing(false);
							} else {
								// console.log("Footer bottom at timeout: ", footerRef.current.style.bottom);
								// console.log("Footer bottom at timeout int: ", parseInt(footerRef.current.style.bottom));
							}
						}
					}, 2000);
				}
			} else {
				
			}
		} catch (error) {
			console.log("Error in handle general scroll: ", error);
		}
	}

	useScript('https://unpkg.com/leaflet@1.9.4/dist/leaflet.css')
	// eslint-disable-next-line @typescript-eslint/no-use-before-define
	useScript('https://unpkg.com/leaflet@1.9.4/dist/leaflet.js');  // we store our css with other css files and load js here
	// eslint-disable-next-line @typescript-eslint/no-use-before-define
	useScript('https://cdnjs.cloudflare.com/ajax/libs/slick-carousel/1.8.1/slick.min.css');
	// eslint-disable-next-line @typescript-eslint/no-use-before-define
	useScript('https://cdnjs.cloudflare.com/ajax/libs/slick-carousel/1.8.1/slick-theme.min.css');

	useEffect(() => {
		// Scroll certain amounts from current position 
		window.scrollBy({ 
			top: 10, // could be negative value
			left: 0, 
			behavior: 'smooth' 
		});
	}, [])

	useEffect(() => {
        // console.log("User plan: ", userPlan);

    }, [userPlan])

	useEffect(() => {
		const fetchUserPlan = async () => {
			// Get user email
			let currentUser: IUserProps = JSON.parse(localStorage.getItem('user'));
			// console.log("User localstorage data: ", currentUser);

			// Check for subscription plan
			if (currentUser != null) {
				let subscriptionResponse: ReturnPropsAPIGetUserSubscriptionPlanResponse = await APIGetUserSubscriptionPlanResponse(currentUser.email);

				// Set user plan from api if api supplies one, else assume free plan
				dispatch(setSubscriptionStatus(subscriptionResponse.subscriberPlan ? subscriptionResponse.subscriberPlan : SubscriptionPlan.Free));
			}
		}
		fetchUserPlan();
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Periodically check if user has a subscription plan.
	useEffect(() => {
		const interval = setInterval(async () => {
			// Get user email
			let currentUser: IUserProps = JSON.parse(localStorage.getItem('user'));
			// console.log("User localstorage data: ", currentUser);			

			// Check for subscription plan
			if (currentUser != null) {
				let subscriptionResponse: ReturnPropsAPIGetUserSubscriptionPlanResponse = await APIGetUserSubscriptionPlanResponse(currentUser.email);

				// Set user plan from api if api supplies one, else assume free plan
				dispatch(setSubscriptionStatus(subscriptionResponse.subscriberPlan ? subscriptionResponse.subscriberPlan : SubscriptionPlan.Free));
				// console.log("User plan: ", subscriptionResponse.subscriberPlan ? subscriptionResponse.subscriberPlan : SubscriptionPlan.Free);
				
			}
		}, 600000);  // 10 minutes * 60 seconds * 1000 milliseconds = 600000 milliseconds
		return () => {
			clearInterval(interval);
		};
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

	return (
		<div 
			className='App' 
			// onContextMenu={(e) => {
            // 	e.preventDefault(); // prevent the default behaviour when right clicked
            // 	console.log("Right Click");
			// 	// TODO: Implement right click menu with redux options and more.	
          	// }}
		  >
			<Suspense fallback={<Loading />}>
				<Header />
				<div 
					className='app-content' 
					ref={appContentRef}
					onScroll={handleContainerScroll} 
					onWheel={handleGeneralScroll}
				>
					<ActionPanel />
					<Routes>
						<Route path='/' element={ <Home /> }/>
						<Route path='/about' element={ <About /> }/>
						<Route path='/services' element={ <Services /> }/>
						<Route path='/dashboard' element={<PrivateRoute><Dashboard userPlan={userPlan}/></PrivateRoute>}>
							<Route path='' element={<GeneralDashboard />}/>
						</Route>
						{/* <Route path='/admin' element={ <Admin /> } /> */}
						<Route path='/login' element={ <LoginForm userPlan={userPlan} apiLoginData={apiLoginData} setApiLoginData={setAPILoginData} /> } />
						<Route path='/login/verify' element={ <LoginVerifyOTPForm userPlan={userPlan} apiLoginData={apiLoginData} /> } />
						<Route path='/privacy-policy' element={ <PrivacyPolicy /> } />
						{/* <Route path='/pricing' element={ <Pricing /> } /> */}
						<Route path='/register' element={ <RegisterForm /> } />
						<Route path='/pre-password-reset' element={ <PasswordPreResetForm /> } />
						<Route path='/password-reset-otp' element={ <PasswordResetOTPForm /> } />
						<Route path='/password-reset' element={ <PasswordResetForm /> } />
						{/* <Route path='/news' element={<PrivateRoute><News /></PrivateRoute>} /> */}
						<Route path='/settings' element={ <Settings /> } >
							<Route index element={ <SettingsAccountInformation /> } />
							<Route path='account-information' element={ <SettingsAccountInformation /> } />
							<Route path='preferences' element={ <SettingsPreferences /> } />
							{/* <Route path='appearance' element={ <SettingsAppearance /> } /> */}
							<Route path='notifications' element={ <SettingsNotifications /> } />
							<Route path='security' element={ <SettingsSecurity /> } />
							<Route path='privacy-and-data' element={ <SettingsPrivacyAndData /> } />
							<Route path='help' element={ <SettingsHelp /> } />
							<Route path='feedback' element={ <SettingsFeedback /> } />
							<Route path='about' element={ <SettingsAbout /> } />
						</Route>
						{/* Look closely below. The dashboards are children of the user dashboard. */}
						<Route path='/weather' element={<PrivateRoute><Weather /></PrivateRoute>} />
						<Route path='*' element={ <RouteNotFound /> } />
					</Routes>
				</div>
				<footer 
					className='app-footer' 
					style={{
						display: (isFooterShowing && location.pathname !== '/login' && location.pathname !== '/login/verify') ? 'flex' : 'none',
						bottom: '-40%'
					}} 
					ref={footerRef}
				>
					<div className='social-container'></div>
					<div className='business-contact'>
						<div className='contact-container'>
							<Button
								props={{
									text: "Contact Us",
									onClick: () => {setContactFormOpen(!isContactFormOpen)},
									classes: 'dark'
								}}
							/>
						</div>
						{isLoggedIn && (
							<div className='feedback-container'>
								<Button
									props={{
										text: "Give Feedback",
										onClick: () => {setFeedbackFormOpen(!isFeedbackFormOpen)},
										classes: 'dark'
									}}
								/>
							</div>
						)}
						<a href='tel:314-964-6438'><i className="fa-solid fa-phone"></i> 314-964-6438</a>
						<a href='mailto:brenton.atchison@lostinedrone.com'><i className="fa-solid fa-envelope"></i> brenton.atchison@lostinedrone.com</a>
						<span><i className="fa-solid fa-location-dot"></i> 12645 Olive Blvd 2nd Floor St. Louis, MO, 63141</span>
					</div>
					<div className='business-information'>
						<div className='business-links'>
							<Link to='/about'>About</Link>| 
							<Link to='/privacy-policy'>Privacy Policy</Link>| 
							<a href='https://form.jotform.com/243178125183052' target='_blank' rel='noreferrer'>Request Refund</a>
						</div>
						<div className='footers-footer'>
							<span className='footer-license-and-insured'>Lostine Drone Co. is FAA Part 107 licensed and insured up to $1 Million</span>
							<span>© 2024 Lostine Drone Co</span>
						</div>
					</div>
				</footer>
				{isContactSubmitFormOpen && (
					<GenericOkayForm title="Submitted!" description="Your contact request has been submitted." isShowingGenericOkayForm={setContactSubmitFormOpen}/>
				)}
				{isFeedbackSubmitFormOpen && (
					<GenericOkayForm title="Submitted!" description="Your feedback has been submitted." isShowingGenericOkayForm={setFeedbackSubmitFormOpen}/>
				)}
				{isContactFormOpen && (
					<>
						<Modal
							title='Contact Us'
							textInfo='Please provide your contact information and a message below.' 
							classes='contact-modal'
							form={<ContactForm isContactSubmitModal={setContactSubmitFormOpen} isModelShowing={setContactFormOpen} />}
						/>
					</>
				)}
				{isFeedbackFormOpen && (
					<>
						<Modal
							title='Feedback'
							textInfo='Please provide feedback below.' 
							classes='feedback-modal'
							form={<FeedbackForm isFeedbackSubmitModal={setFeedbackSubmitFormOpen} isModelShowing={setFeedbackFormOpen} />}
						/>
					</>
				)}
			</Suspense>
		</div>
	);
}

export default App;
